import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, X } from "lucide-react";

/**
 * Multi-trustee role manager.
 * Lets the primary admin assign any registered member a granular trustee
 * portfolio: Chair, Secretary, Treasurer, DSL, Education Lead, or generic
 * Trustee. The chosen role is shown in /admin/trustees and used by the
 * front-end to gate certain UI panels (CIO, Governance, Education).
 */
const ROLE_LABELS = {
  chair: "Chair",
  secretary: "Secretary",
  treasurer: "Treasurer",
  dsl: "Designated Safeguarding Lead",
  education_lead: "Education Lead",
  trustee: "Trustee (general)",
};

export default function TrusteeRolesManager() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [picker, setPicker] = useState({ user_id: "", trustee_role: "trustee" });
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [t, m] = await Promise.all([
        api.get("/admin/trustees"),
        api.get("/admin/members"),
      ]);
      setItems(t.data.items || []);
      setAvailable(t.data.available_roles || []);
      setMembers(m.data.members || []);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const setRole = async (user_id, trustee_role) => {
    setBusy(user_id);
    try {
      await api.post("/admin/trustees/role", { user_id, trustee_role });
      toast.success(trustee_role ? `Role set to ${ROLE_LABELS[trustee_role] || trustee_role}` : "Role cleared");
      setPicker({ user_id: "", trustee_role: "trustee" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(null);
  };

  const assignable = members.filter(
    (m) => m.role !== "admin" && !items.find((i) => i.user_id === m.user_id && i.trustee_role)
  );

  return (
    <div className="space-y-6" data-testid="trustees-manager">
      <div>
        <h3 className="font-display text-xl text-navy flex items-center gap-2">
          <ShieldCheck size={18} className="text-saffron" /> Trustee portfolios
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Assign granular trustee responsibilities. The primary admin (Founder) retains
          full system access. Trustee role labels appear on the public Governance page
          and gate access to sensitive panels (CIO, Safeguarding, Education).
        </p>
      </div>

      <div className="bg-white border border-navy/10 rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Assign a role</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={picker.user_id}
            onChange={(e) => setPicker((p) => ({ ...p, user_id: e.target.value }))}
            data-testid="trustee-user-select"
            className="border border-navy/15 rounded-sm px-3 py-2 text-sm min-w-[260px]"
          >
            <option value="">— Select a member —</option>
            {assignable.map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.email})</option>
            ))}
          </select>
          <select
            value={picker.trustee_role}
            onChange={(e) => setPicker((p) => ({ ...p, trustee_role: e.target.value }))}
            data-testid="trustee-role-select"
            className="border border-navy/15 rounded-sm px-3 py-2 text-sm"
          >
            {available.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
            ))}
          </select>
          <button
            onClick={() => setRole(picker.user_id, picker.trustee_role)}
            disabled={!picker.user_id || busy === picker.user_id}
            data-testid="trustee-assign-btn"
            className="px-4 py-2 bg-saffron text-navy rounded-sm text-xs font-medium hover:bg-saffron-light disabled:opacity-50"
          >
            Assign role
          </button>
        </div>
      </div>

      <div>
        <h4 className="font-display text-lg text-navy mb-3">Current trustees & admins ({items.length})</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="trustees-list">
            <thead>
              <tr className="text-left border-b border-navy/10 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="py-2 pr-3">Member</th>
                <th className="py-2 pr-3">System role</th>
                <th className="py-2 pr-3">Trustee portfolio</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.user_id} className="border-b border-navy/5">
                  <td className="py-2 pr-3 text-navy">{u.name || u.email}<div className="text-[10px] text-slate-400">{u.email}</div></td>
                  <td className="py-2 pr-3 text-xs">
                    {u.role === "admin" ? (
                      <span className="px-2 py-0.5 bg-navy/10 text-navy rounded-sm">Admin</span>
                    ) : (
                      <span className="text-slate-400">Member</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {u.trustee_role ? (
                      <span className="px-2 py-0.5 bg-saffron/15 text-saffron rounded-sm">{ROLE_LABELS[u.trustee_role] || u.trustee_role}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {u.trustee_role && (
                      <button
                        onClick={() => setRole(u.user_id, null)}
                        data-testid={`trustee-clear-${u.user_id}`}
                        className="text-xs text-red-600 underline inline-flex items-center gap-1"
                      >
                        <X size={11} /> Clear role
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="py-3 italic text-slate-500">No trustees yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
